import React, { useMemo, useState } from 'react';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ToolPart } from '@cotera/sdk/core';
import { cleanString } from './web-tool-renderer-utils';

/**
 * Error text for a finished tool part, covering both failure channels: the AI
 * SDK's `output-error` state (`errorText`) and tools that complete "normally"
 * with an error envelope in their output. Returns undefined when the part
 * didn't fail, or failed without any usable detail.
 */
export function toolErrorText(part: ToolPart): string | undefined {
  const errorText = cleanString(part.toolPart.errorText);
  if (errorText !== undefined) {
    return errorText;
  }
  if (!part.isError()) {
    return undefined;
  }
  const output = part.getOutput<Record<string, unknown>>();
  if (output === undefined || output === null) {
    return undefined;
  }
  const message = cleanString(output['error']);
  if (message !== undefined) {
    return message;
  }
  return formatJson(output);
}

/** Pretty-printed JSON, or undefined when the value is empty or cyclic. */
function formatJson(value: Record<string, unknown>): string | undefined {
  if (Object.keys(value).length === 0) {
    return undefined;
  }
  try {
    return cleanString(JSON.stringify(value, null, 2));
  } catch {
    return undefined;
  }
}

const SECTION_HEADING_CLASS =
  'text-xs font-medium uppercase tracking-wide text-muted-foreground';

const PAYLOAD_CLASS =
  'max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-md p-2 font-mono text-xs';

/**
 * Shared failure row for tool renderers. Collapses a failed tool call to a
 * single quiet line (only the icon is destructive, so a run of failures doesn't
 * shout) and expands to show the tool input and the raw error text.
 */
export const ToolErrorRow: React.FC<{
  /** Collapsed line, e.g. `Search for “shapewear” encountered an error`. */
  title: string;
  /** Verbatim error text; rendered raw in the expanded panel. */
  errorText: string | undefined;
  /** Tool input, pretty-printed in the expanded panel. */
  input: Record<string, unknown>;
  /**
   * Leading provider/type icon, rendered before the destructive status icon so
   * the row lines up with tool rows that show one (external tools, recall, etc).
   */
  icon?: React.ReactNode;
}> = ({ title, errorText, input, icon }) => {
  const [expanded, setExpanded] = useState(false);
  const inputJson = useMemo(() => formatJson(input), [input]);

  return (
    <div className="flex flex-col gap-2 py-1 text-sm">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="group flex w-full items-center justify-between gap-4 text-left text-muted-foreground hover:text-foreground focus:outline-none"
      >
        <span className="flex min-w-0 items-center gap-2">
          {icon !== undefined ? icon : null}
          <XIcon className="h-4 w-4 shrink-0 text-destructive" />
          <span className="truncate italic">{title}</span>
        </span>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 shrink-0 transition-transform',
            expanded ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>

      {expanded ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3">
          {inputJson !== undefined ? (
            <section className="flex flex-col gap-1">
              <h4 className={SECTION_HEADING_CLASS}>Input</h4>
              <pre className={cn(PAYLOAD_CLASS, 'bg-muted/50 text-foreground')}>
                {inputJson}
              </pre>
            </section>
          ) : null}
          <section className="flex flex-col gap-1">
            <h4 className={SECTION_HEADING_CLASS}>Error</h4>
            {errorText !== undefined ? (
              <pre
                className={cn(
                  PAYLOAD_CLASS,
                  'bg-destructive/10 text-destructive'
                )}
              >
                {errorText}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">
                No error details were returned.
              </p>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
};
